import React from "react";
import { motion } from "framer-motion";
import { Gem, Filter, Search, CalendarClock, Check } from "lucide-react";
import { motionOk } from "../lib/motionOk.js";
import { useLang } from "../i18n.jsx";

const iconMap = { gem: Gem, filter: Filter, search: Search, calendar: CalendarClock };

export default function AuthorityPillars() {
  const { content } = useLang();
  const { pillars, pillarsHead } = content;
  return (
    <section id="pilar" className="relative px-6 py-20 overflow-hidden">
      <div className="pointer-events-none absolute -top-10 right-0 w-[360px] h-[360px] rounded-full bg-acid/[0.03] blur-3xl" />
      <div className="relative mx-auto max-w-7xl">
        <motion.div
          initial={motionOk ? { opacity: 0, y: 24 } : false}
          whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-2xl"
        >
          <p className="mb-4 text-[12px] uppercase tracking-[0.3em] text-acid">{pillarsHead.eyebrow}</p>
          <h2 className="font-display font-extrabold tracking-tight text-[clamp(1.8rem,4.2vw,3.2rem)] leading-[1.08]">
            {pillarsHead.titleA}
            <span className="block">{pillarsHead.titleB}</span>
          </h2>
        </motion.div>

        <div className="mt-12 grid gap-6 md:grid-cols-2">
          {pillars.map((p, i) => {
            const Icon = iconMap[p.icon] || Gem;
            return (
              <motion.div
                key={p.num}
                initial={motionOk ? { opacity: 0, y: 30 } : false}
                whileInView={motionOk ? { opacity: 1, y: 0 } : undefined}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ delay: (i % 2) * 0.08, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="group relative rounded-3xl border border-paper/10 bg-ink2/60 p-8 transition-colors hover:border-acid/40"
              >
                <div className="flex items-start justify-between">
                  <span className="grid place-items-center w-12 h-12 rounded-2xl bg-acid/10 text-acid">
                    <Icon className="w-6 h-6" />
                  </span>
                  <span className="font-display text-4xl font-extrabold text-paper/12 transition-colors group-hover:text-acid/25">{p.num}</span>
                </div>
                <h3 className="mt-6 font-display text-xl font-bold tracking-tight text-paper">{p.title}</h3>
                <p className="mt-3 text-paper/70 text-[15px] leading-relaxed">{p.desc}</p>
                <ul className="mt-5 space-y-2">
                  {p.points.map((pt) => (
                    <li key={pt} className="flex items-center gap-2.5 text-[15px] text-paper/70">
                      <Check className="w-4 h-4 text-acid shrink-0" /> {pt}
                    </li>
                  ))}
                </ul>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
